import React from "react";

interface LegendItem {
  id?: string;
  name: string;
  color?: string;
  /** Truncated label to render instead of `name` — `name` still backs click
   * identity, unaffected by display overrides. */
  displayName?: string;
  /** Full (untruncated) resolved label shown in the hover title — falls back
   * to `displayName`, then `name`, when omitted. */
  titleText?: string;
  /** Optional second line rendered below the label (e.g. "N: 1,234"). */
  secondaryText?: string;
}

interface ChartLegendSidebarProps {
  legendItems: LegendItem[];
  visibleItems: Record<number, boolean> | Set<string>;
  onLegendClick: (indexOrName: number | string) => void;
  // For bar charts, we use name-based visibility; for donut, index-based
  useNameBasedVisibility?: boolean;
}

export const ChartLegendSidebar: React.FC<ChartLegendSidebarProps> = ({
  legendItems,
  visibleItems,
  onLegendClick,
  useNameBasedVisibility = false,
}) => {
  const getIsVisible = (item: LegendItem, index: number): boolean => {
    if (useNameBasedVisibility) {
      // Check by ID first if available, otherwise by name
      const identifier = item.id || item.name;
      return (visibleItems as Set<string>).has(identifier);
    }
    return (visibleItems as Record<number, boolean>)[index] !== false;
  };

  return (
    <div className="bg-white border-r border-[#E5E7EB] z-10 flex flex-col">
      {/* Scrollable Content */}
      <div className="flex-1 overflow-y-auto overflow-x-hidden px-3 py-3 w-[240px]">
        <div className="flex flex-col gap-1.5">
          {legendItems.map((item, index) => {
            const isVisible = getIsVisible(item, index);
            const clickValue = useNameBasedVisibility
              ? item.id || item.name
              : index;

            return (
              <div
                key={useNameBasedVisibility ? item.id || item.name : index}
                onClick={() => onLegendClick(clickValue)}
                className={`
                  flex items-center gap-2.5 cursor-pointer px-2 py-2 rounded
                  transition-all duration-200 hover:bg-[#F3F4F6]
                  ${isVisible ? "opacity-100" : "opacity-50"}
                `}
              >
                <div
                  className={`
                    w-1.5 h-1.5 rounded-sm flex-shrink-0 box-border
                    ${isVisible ? "" : "border-2 border-[#9CA3AF]"}
                  `}
                  style={{
                    backgroundColor: isVisible
                      ? item.color || "#57bfe7"
                      : "transparent",
                  }}
                />
                <div className="flex flex-col flex-1 overflow-hidden">
                  <span
                    className={`
                      text-[13px] leading-[18px] text-[#374151] overflow-hidden text-ellipsis whitespace-nowrap
                      ${isVisible ? "font-normal" : "line-through"}
                    `}
                    title={item.titleText ?? item.displayName ?? item.name}
                  >
                    {item.displayName ?? item.name}
                  </span>
                  {item.secondaryText && (
                    <span className="text-[11px] leading-[14px] text-[#6B7280] overflow-hidden text-ellipsis whitespace-nowrap">
                      {item.secondaryText}
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};
